
import { defineComponent, PropType } from 'vue';
import CxSpinner from '@/components/CxSpinner.vue';
import CxPasswordNote from '@/components/CxPasswordNote.vue';
import { ErrorType } from '@/composables/useDisplayError';
import { getStrength, isStrongEnough } from '@/services/password.service';


export default defineComponent({
  name: 'CxResetForm',
  components: { CxSpinner, CxPasswordNote },
  props: {
    username: { type: Object as PropType<string | null> },
    resetError: { type: Object as PropType<ErrorType | null> },
    isResetQueryInProgress: { type: Boolean }
  },
  emits: ['reset'],
  data(): {
    password1: string | null;
    password2: string | null;
    pwStr: number;
    pwStrClass: string | null;
    pwStrDescription: string | null;
    minPwStr: number;
    } {
    return {
      password1: null,
      password2: null,
      pwStr: 0,
      pwStrClass: null,
      pwStrDescription: null,
      minPwStr: 75,
    };
  },
  computed: {
    translations(): {
      hi: string;
      choose: string;
      password1: string;
      password2: string;
      set: string;
      passwordsDoNotMatch: string;
      serviceUnavailable: string;
      unexpectedError: string;
      pwStr: string;
      } {
      return {
        hi: this.$gettext('Hi,'),
        choose: this.$gettext('Please choose your new password.'),
        password1: this.$gettext('New password:'),
        password2: this.$gettext('New password (confirm):'),
        set: this.$gettext('Set new password'),
        passwordsDoNotMatch: this.$gettext('Passwords didn\'t match'),
        serviceUnavailable: this.$gettext('Sorry, service is unavailable - please try again later'),
        unexpectedError: this.$gettext('Unexpected error has occurred - please try again later or contact support team'),
        pwStr: this.$gettext('Password strength'),
      };
    },
    resetErrorText(): string | null {
      if (!this.resetError) return null;
      if (this.resetError === ErrorType.PasswordsDoNotMatch) return this.translations.passwordsDoNotMatch;
      if (this.resetError === ErrorType.ServiceUnavailable) return this.translations.serviceUnavailable;
      if (this.resetError === ErrorType.UnexpectedError) return this.translations.unexpectedError;
      return null;
    }
  },
  methods: {
    resetPassword(event: Event): void {
      if (event) event.preventDefault();
      const { password1, password2 } = this;
      if (password1 && password2) this.$emit('reset', { password1, password2 });
    },
    handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter') this.resetPassword(event);
    },
    checkPasswordStrength() {
      if (!this.password1) return;
      const pwStr = getStrength(this.password1);
      if (pwStr < this.minPwStr) {
        this.pwStrClass = 'danger';
        this.pwStrDescription = this.$gettext('Weak');
      } else {
        this.pwStrClass = 'success';
        this.pwStrDescription = this.$gettext('Strong');
      }
      this.pwStr = pwStr;
    },
    checkPasswordIsStrongEnough(): boolean {
      if (!this.password1) return false;
      return isStrongEnough(this.password1, this.minPwStr);
    }
  },
  watch: {
    password1: 'checkPasswordStrength',
  },
});
